<template>
  <div id="settings" v-show="editorMode === 'settings'">
    <div style="padding: 1px;">
      <div class="ui top attached info message" v-if="flow.publication">
        <p>{{ $t('FLOW_IS_DOWNLOADED', { version: flow.publication.releaseNote.version }) }}</p>
      </div>
      <PublicationGetter
        :appId="app.id"
        :flow="flow"
        :condensed="true"
        :showAbstract="true"
        :hideFlowLink="true"
        :topAttached="true"
      />
    </div>

    <fieldset>
      <!-- DETAILS -->
      <h3 class="ui horizontal left aligned divider header">{{ $t('FLOW_DETAILS_HEADER') }}</h3>

      <div class="ui basic segment">
        <div class="two fields">
          <!-- Name -->
          <div
            class="field required"
            :class="{
              error: $parent.$v.flow.name.$error,
              info: copied && !$parent.$v.flow.name.$error
            }"
          >
            <label for="flowName">{{ $t('NAME') }}</label>
            <input
              id="flowName"
              type="text"
              :placeholder="$t('NAME')"
              v-model.trim="flowName"
              :disabled="isEditDisabled"
              ref="nameInput"
            />
          </div>
          <!-- ID -->
          <div class="field" v-if="flow.id">
            <label for="flowIdentifier">{{ $t('IDENTIFIER') }}</label>
            <input
              id="flowIdentifier"
              type="text"
              :placeholder="$t('IDENTIFIER')"
              v-model.trim="flowId"
              disabled="disabled"
            />
          </div>
        </div>

        <div class="inline fields">
          <!-- TYPE -->
          <label>{{ $tc('TYPE', 1) }}</label>
          <div class="field" v-for="(config, type) in defaultTypeConfig" :key="type">
            <div class="ui radio checkbox">
              <input
                type="radio"
                name="flowType"
                :id="'flowType' + type"
                :value="type"
                v-model="flowType"
                disabled="disabled"
              />
              <label :for="'flowType' + type">{{ $tc(type, 1) }}</label>
            </div>
          </div>
        </div>

        <div class="fields">
          <div class="two wide field" v-if="flow.type === 'STATE'">
            <typeahead
              :shouldFilterItems="false"
              :items="actionTypes"
              :current-item="flowTypeConfig.action"
              :label-text="$tc('ACTION', 1)"
              :placeholder-text="$tc('ACTION', 1)"
              :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('ACTION', 2) })"
              :select-handler="actionTypeSelected"
              :remove-handler="actionTypeRemoved"
              :disabled="isEditDisabled"
            ></typeahead>
          </div>
          <div
            class="field"
            v-for="(config, configProperty) in defaultTypeConfig[flow.type]"
            :key="configProperty"
            v-if="typeof config === 'string' && shouldDisplayGenericInputFor(configProperty)"
          >
            <label :for="'flow' + configProperty">{{ $tc(configProperty.toUpperCase(), 1) }}</label>
            <input
              :id="'flow' + configProperty"
              type="text"
              :placeholder="$tc(configProperty.toUpperCase(), 1)"
              :value="flowTypeConfig[configProperty]"
              @input="updateTypeConfigField($event.target.value, configProperty)"
              :disabled="isEditDisabled"
            />
          </div>

          <!-- MANDATORY STATE -->
          <div
            class="field"
            v-if="flow.type === 'STATE' && flow.typeConfig && flow.typeConfig.action == 'SET'"
          >
            <div class="ui toggle checkbox">
              <input
                id="mandatoryState"
                type="checkbox"
                v-model="flowIsMandatory"
                :disabled="isEditDisabled"
              />
              <label for="mandatoryState">
                {{ $t('MANDATORY_STATE') }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- INPUT/OUTPUT -->
      <template v-if="flow.type !== 'STATE'">
        <h4 class="ui header">{{ $t('INPUT_AND_OUTPUT_HEADER') }}</h4>
        <div class="ui basic segment">
          <div class="field">
            <div class="two fields">
              <div class="eight wide field">
                <flow-inputs
                  :inputs="flow.inputs"
                  :add-handler="addInput"
                  :remove-handler="removeInput"
                  :isDisabled="isEditDisabled || isCodeEditDisabled"
                ></flow-inputs>
              </div>

              <div class="eight wide field">
                <flow-outputs
                  :outputs="flow.outputs"
                  :available-outputs="availableOutputs"
                  :add-handler="addOutput"
                  :remove-handler="removeOutput"
                  :isDisabled="isEditDisabled || isCodeEditDisabled"
                ></flow-outputs>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ANCHORS -->
      <h4 class="ui header" v-if="flow.type === 'ACTION'">{{ $t('ANCHOR_HEADER') }}</h4>
      <div class="ui basic segment">
        <div class="field toggleField" v-if="flow.type === 'ACTION'">
          <div class="ui toggle checkbox">
            <input
              id="hideFromUser"
              type="checkbox"
              v-model="flowHideFromUser"
              :disabled="isEditDisabled"
            />
            <label for="hideFromUser">
              {{ $t('HIDE_FROM_USER', { thing: $tc('FLOW', 1) }) }}
            </label>
          </div>
        </div>
        <div class="field" v-if="flow.type === 'ACTION'">
          <typeahead
            :multiple="true"
            :max-items="999"
            :current-items="anchorFields"
            :items="anchorableFields"
            :label-text="$tc('ANCHOR', 2)"
            :placeholder-text="$tc('ANCHOR', 1)"
            :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('ANCHOR', 2) })"
            :select-handler="anchorSelected"
            :remove-handler="anchorRemoved"
            :disabled="isEditDisabled || flowHideFromUser"
          >
            <template slot="selected" slot-scope="props">
              {{ props.item.name }}
              <span class="detail">{{ nameOfApp(props.item.application) }}</span>
            </template>
            <template slot="dropdown" slot-scope="props">
              {{ props.item.name }}
              <span class="subtle">{{ nameOfApp(props.item.application) }}</span>
            </template>
          </typeahead>
        </div>
      </div>

      <!-- OWNERSHIP -->
      <template>
        <h4 class="ui header">{{ $t('OWNERSHIP_HEADER') }}</h4>
        <div class="ui basic segment">
          <!-- GROUPS -->
          <div class="field">
            <group-typeahead
              :allGroups="allGroups"
              :currentGroups="editedGroups"
              @selected="flowGroupSelected"
              @removed="groupRemoved"
              @created="flowGroupCreated"
              :disabled="!allGroups || isEditDisabled"
              v-if="!isModuleTypeFlow"
            />
          </div>
          <!-- MAINTAINER AND LAST_EDITED_BY -->
          <div class="two fields" v-if="!isCodeEditDisabled">
            <div class="eight wide field">
              <typeahead
                :items="allUsers"
                :current-item="flowUser.name"
                :label-text="$tc('MAINTAINER', 1)"
                :placeholder-text="$tc('MAINTAINER', 1)"
                :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('MAINTAINER', 2) })"
                :select-handler="maintainerSelected"
                :remove-handler="maintainerRemoved"
                :disabled="isMaintainerEditDisabled"
              />
            </div>

            <div class="eight wide field">
              <label>{{ $t('LAST_EDITED_BY') }}</label>
              <div class="inputField">
                <div class="inputFieldName content ellipsis">
                  {{ lastEditedBy }}
                </div>
                <div class="inputFieldImage">
                  <profile-icon :user="lastEditedByUser" :size="'big'" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="app.type !== 'CONTEXTPARTICIPANT' && !isCodeEditDisabled">
        <div class="ui grid">
          <div class="eight wide column">
            <!-- AUTOCOMPLETION -->
            <h4 class="ui header">{{ $t('AUTOCOMPLETION') }}</h4>
            <div class="ui basic segment">
              <div class="field">
                <typeahead
                  :items="versions"
                  :current-item="flow.compatibleVersion"
                  :label-text="$t('COMPATIBLE_WITH')"
                  :placeholder-text="$t('COMPATIBLE_WITH')"
                  :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('VERSION', 2) })"
                  :select-handler="versionSelected"
                  :remove-handler="versionRemoved"
                  :disabled="isEditDisabled"
                />
              </div>
            </div>
          </div>
          <div class="eight wide column" v-if="flow.type !== 'MODULE'">
            <!-- RUNTIME PROPERTIES -->
            <h4 class="ui header">
              {{ $t('RUNTIME_PROPERTIES') }}
            </h4>
            <div class="ui basic segment">
              <div class="field toggleField">
                <div class="ui toggle checkbox">
                  <input
                    id="precompile"
                    type="checkbox"
                    v-model="flowPrecompile"
                    :disabled="isEditDisabled"
                  />
                  <label for="precompile">
                    {{ $t('PRECOMPILE_FLOW') }}
                  </label>
                </div>
              </div>
              <div
                class="field toggleField"
                v-if="flow.type === 'STATE' && flow.typeConfig && flow.typeConfig.action == 'SET'"
              >
                <div class="ui toggle checkbox">
                  <input
                    id="restoreFocus"
                    type="checkbox"
                    v-model="flowRestoreFocus"
                    :disabled="isEditDisabled"
                  />
                  <label for="restoreFocus">
                    {{ $t('RESTORE_FOCUS_AFTER_FLOW') }}
                  </label>
                </div>
              </div>
              <div class="inline fields">
                <!-- LANGUAGE -->
                <label>{{ $tc('LANGUAGE', 1) }}</label>
                <div class="field" v-for="(name, capsName) in supportedLanguages" :key="capsName">
                  <div class="ui radio checkbox">
                    <input
                      type="radio"
                      name="flowLanguage"
                      :id="'flowLanguage' + name"
                      :value="name"
                      v-model="flowLanguage"
                      disabled="disabled"
                    />
                    <label :for="'flowLanguage' + name">{{ $tc('LANGUAGE_' + capsName, 1) }}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- TRIGGERS -->
      <template v-if="flow.type === 'ACTION'">
        <h3 class="ui horizontal left aligned divider header">{{ $t('TRIGGER_HEADER') }}</h3>
        <div class="ui basic segment">
          <flow-triggers :isDisabled="isEditDisabled"></flow-triggers>
        </div>
      </template>

      <!-- USED IN -->
      <template v-if="flow.type !== 'STATE'">
        <h4 class="ui horizontal left aligned header">
          {{ $t('USED_IN', { thing: $tc('FLOW', flowsUsingThisFlow.length) }) }}
        </h4>
        <div class="ui basic segment">
          <div class="flowsUsingThisFlowList">
            <div class="field" v-if="flowsUsingThisFlow.length > 0">
              <div class="ui link list" id="flowList">
                <router-link
                  class="item"
                  :to="{
                    name: 'app-flow',
                    params: { appId: f.application, flowId: f.id, mode: 'auto' }
                  }"
                  :key="f.id"
                  v-for="f in flowsUsingThisFlow"
                >
                  <span>
                    <i class="right triangle icon"></i>
                    <span class="content">{{ f.name }}</span>
                  </span>
                </router-link>
              </div>
            </div>
            <div v-else class="flowsUsingThisFlowList">
              <span class="text bold">{{ $t('FLOW_NOT_USED') }}</span>
            </div>
          </div>
        </div>
      </template>
    </fieldset>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';

#settings {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;

  .subtle {
    color: gray;
  }

  .flowsUsingThisFlowList {
    margin-bottom: 20px;
  }

  .toggleField {
    margin-top: 1.5em;
    margin-bottom: 1.5em;
  }

  .inputField {
    border: 1px solid lightgrey;
    border-radius: 5px;
    height: 38px;
    display: flex;
    width: 100%;
  }

  .inputFieldName {
    margin-top: auto;
    margin-bottom: auto;
    padding-left: 14px;
    flex: 1;
  }

  .inputFieldImage {
    padding: 4px 2px 4px 4px;
    flex: 0 0 36px;
    float: right;
  }
}
</style>

<script>
// @flow
import Vue from 'vue'
import { FlowEditor, Toast, Group, Flow, CodeCompletions, Hub } from 'services'
import { Typeahead, GroupTypeahead } from 'ui'
import { PublicationGetter } from './../../Hub'
import FlowInputs from './FlowInputs'
import FlowOutputs from './FlowOutputs'
import FlowTriggers from './FlowTriggers'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import ProfileIcon from 'src/components/Users/ProfileIcon.vue'

export default {
  name: 'flow-settings',
  components: {
    Typeahead,
    GroupTypeahead,
    FlowInputs,
    FlowOutputs,
    FlowTriggers,
    PublicationGetter,
    ProfileIcon
  },

  props: {
    flowUser: {
      type: Object,
      default: () => {}
    },
    copied: {
      type: Boolean,
      default: () => false
    },
    allGroups: {
      type: Array
    },
    savedGroups: {
      type: Array
    },
    editedGroups: {
      type: Array
    },
    groupSelected: {
      type: Function
    },
    groupCreated: {
      type: Function
    },
    groupRemoved: {
      type: Function
    }
  },

  computed: {
    ...mapFields('flowEditor', {
      flowName: 'flow.name',
      flowId: 'flow.id',
      flowType: 'flow.type',
      flowLanguage: 'flow.language',
      flowMaintainer: 'flow.maintainer',
      variables: 'variables',
      flowTypeConfig: 'flow.typeConfig',
      flowAnchors: 'flow.anchors',
      flowOutputs: 'flow.outputs',
      flowInputs: 'flow.inputs',
      flowHideFromUser: 'flow.hideFromUser',
      flowPrecompile: 'flow.precompile',
      flowRestoreFocus: 'flow.restoreFocus',
      flowCompatibleVersion: 'flow.compatibleVersion',
      flowIsMandatory: 'flow.isMandatory',
      lastEditedBy: 'lastEditedBy'
    }),
    ...mapGetters('flowEditor', ['isManagedApp']),
    ...mapGetters(['userPreferences']),
    editorMode() {
      return this.$store.state.flowEditor.editorMode
    },
    flow() {
      return this.$store.state.flowEditor.flow || {}
    },
    app() {
      return this.$store.state.flowEditor.app || {}
    },
    allApps() {
      return this.$store.state.application.items
    },
    allFields() {
      return this.$store.state.field.items
    },
    allUsers() {
      return this.$store.state.user.items
    },
    lastEditedByUser() {
      return _.find(this.allUsers, user => {
        return user.name === this.lastEditedBy
      })
    },
    allFlows() {
      return this.$store.state.flow.items
    },
    isCurrentUserAllowedToEditFlow() {
      return this.$store.state.flowEditor.isCurrentUserAllowedToEditFlow
    },
    availableOutputs() {
      if (!this.flow.outputs) return []
      const flowOutputNames = this.flow.outputs.map(o => o.name)
      return this.variables.filter(v => flowOutputNames.indexOf(v.name) === -1)
    },
    anchorFields() {
      return _.filter(this.allFields, field => _.includes(this.flowAnchors, field.id))
    },
    anchorableFields() {
      return _.filter(this.allFields, field => field.path && !field.path.startsWith('@'))
    },
    flowsUsingThisFlow() {
      return _.filter(this.allFlows, f => _.includes(f.usesFlows, this.flow.id)) || []
    },
    isModuleTypeFlow() {
      return this.flow.type === 'MODULE'
    },
    isMaintainerEditDisabled() {
      return this.isEditDisabled && this.isCurrentUserAllowedToEditFlow
    },
    isEditDisabled() {
      return this.$store.state.flowEditor.isFlowEditingDisabled
    },
    isCodeEditDisabled() {
      return (
        !_.isEmpty(this.flow.publication) &&
        this.flow.publication.type === Hub.INLINED_FLOW_PUBLICATION
      )
    }
  },

  data() {
    return {
      actionTypes: [{ id: 'GET', name: 'GET' }, { id: 'SET', name: 'SET' }],
      defaultTypeConfig: Flow.defaultFlowTypeConfig,
      supportedLanguages: Flow.supportedLanguages,
      versions: []
    }
  },

  methods: {
    versionSelected(version) {
      this.flowCompatibleVersion = version.id
    },
    versionRemoved() {
      this.flowCompatibleVersion = null
    },
    shouldDisplayGenericInputFor(name: string) {
      return FlowEditor.shouldDisplayGenericInputFor(name)
    },

    nameOfApp(appId: string): string {
      let app = _.find(this.allApps, app => app.id === appId)
      return (app && app.name) || ''
    },

    checkGroupOnApp(group: Object) {
      if (!_.includes(group.application, this.app.id)) {
        if (!this.userPreferences.autoAddAppGroup)
          Toast.warning(this.$i18n.t('GROUP_NOT_IN_APPLICATION', { group: group.name }))
        else if (
          confirm(
            this.$i18n.t('GROUP_ADD_TO_APP_QUESTION', { app: this.app.name, group: group.name })
          )
        ) {
          Group.addAffiliate('application', group.id, this.app.id).catch(err => {
            Toast.error(
              this.$i18n.t('GROUP_ADD_TO_APP_FAILURE', { app: this.app.name, group: group.name })
            )
            console.error(err)
          })
        }
      }
    },

    flowGroupSelected(group: Object) {
      this.checkGroupOnApp(group)
      this.groupSelected(group)
    },

    flowGroupCreated(groupName: string) {
      return this.groupCreated(groupName).then(newGroup => {
        this.checkGroupOnApp(newGroup)
      })
    },

    actionTypeSelected(action) {
      this.flowTypeConfig = {
        ...this.flowTypeConfig,
        action: action.name
      }
    },

    actionTypeRemoved() {
      this.flowTypeConfig = {
        ...this.flowTypeConfig,
        action: ''
      }
    },

    anchorSelected(anchor: Object) {
      this.flowAnchors = _.compact(_.uniq(_.concat(this.flow.anchors, anchor.id))) || []
    },

    anchorRemoved(anchor: Object) {
      this.flowAnchors = _.compact(_.reject(this.flow.anchors, id => id === anchor.id)) || []
    },

    maintainerSelected(maintainer: Object) {
      this.flowMaintainer = maintainer.id
    },

    maintainerRemoved() {
      this.flowMaintainer = ''
    },

    addInput(input: Object) {
      const exists = this.flowInputs.some(i => i.name === input.name)
      if (!exists) this.flowInputs = [...this.flowInputs, input]
    },

    removeInput(input: Object) {
      this.flowInputs = this.flowInputs.filter(i => i.name !== input.name)
    },

    addOutput(output: Object) {
      const exists = this.flow.outputs.some(o => o.name === output.name)
      if (!exists) this.flowOutputs = [...this.flowOutputs, output]
    },

    removeOutput(output: Object) {
      this.flowOutputs = this.flowOutputs.filter(o => o.name !== output.name)
    },
    updateTypeConfigField(value, field) {
      this.flowTypeConfig = {
        ...this.flowTypeConfig,
        [field]: value || ''
      }
    }
  },

  mounted() {
    CodeCompletions.getVersions().then(versions => {
      console.debug('versions', versions)
      this.versions = _.map(versions, v => ({ name: v, value: v, id: v }))
    })
    this.$nextTick(() => {
      if (this.copied) this.$refs.nameInput.focus()
    })
  }
}
</script>
